import { html, nothing, type TemplateResult } from "lit-html";

import { api } from "../api.js";
import { bootstrapGenesisLocal, decryptItem, encryptItem, type UnlockedState } from "../client-crypto.js";
import { copyToClipboard, rerender, showToast, state as appState } from "../main.js";

const t = (en: string, jp: string): string => (appState.lang === "en" ? en : jp);

type Tab = "users" | "invites" | "sessions" | "devices" | "links" | "categories" | "audit";

interface CategoryRow {
  id: string;
  slug: string;
  label_en: string;
  label_jp: string;
  icon_name: string;
  default_brand_color: string;
  order_index: number;
}

interface UsefulLinkRow {
  id: string;
  label_en: string;
  label_jp: string | null;
  url: string;
  category: string | null;
  icon_name: string | null;
  order_index: number;
}

interface SessionRow {
  id: string;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  device_id: string | null;
  ip: string | null;
  ua: string | null;
  created_at: number;
  last_used_at: number;
  expires_at: number;
}

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: string;
  totp_enrolled_at: number | null;
  is_blocked?: 0 | 1;
  must_change_pw: boolean;
  reset_pending: boolean;
  created_at: number;
}

interface DeviceRow {
  id: string;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  device_label: string | null;
  device_name: string | null;
  user_agent: string | null;
  approved_at: number | null;
  approved_by: string | null;
  last_used_at: number | null;
  revoked_at: number | null;
  created_at: number;
  status: "approved" | "pending" | "revoked";
}

interface AuditRow {
  id: number;
  at: number;
  actor_id: string | null;
  action: string;
  item_id: string | null;
  ip: string | null;
  meta_json: string | null;
}

const s = {
  tab: "users" as Tab,
  loaded: { users: false, devices: false, audit: false, sessions: false, links: false, categories: false } as Record<string, boolean>,
  users: [] as UserRow[],
  devices: [] as DeviceRow[],
  audit: [] as AuditRow[],
  sessions: [] as SessionRow[],
  links: [] as UsefulLinkRow[],
  categories: [] as CategoryRow[],
  inviteEmail: "",
  inviteName: "",
  invitePersonal: "",
  inviteRole: "EMPLOYEE" as "EMPLOYEE" | "ADMIN",
  inviteOut: "",
  // Useful link editor state
  newLink: { label_en: "", label_jp: "", url: "", category: "", icon_name: "" },
  editingLink: null as UsefulLinkRow | null,
  // Provision (SU only) form state. Admin/Employee don't have a separate
  // recovery email — their primary mail is used everywhere. So the form
  // only collects name + email + role + optional temp password.
  provName: "",
  provEmail: "",
  provRole: "EMPLOYEE" as "EMPLOYEE" | "ADMIN",
  provPassword: "",
  provResult: null as null | { email: string; password: string; role: string },
  busy: false,
};

function generateRandomPassword(len = 18): string {
  // 18 chars from a friendly alphabet (no ambiguous l/I/1/O/0). Drawn
  // from crypto.getRandomValues so the SU's generated password is
  // genuinely random, not predictable.
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*";
  const bytes = crypto.getRandomValues(new Uint8Array(len));
  let out = "";
  for (let i = 0; i < len; i++) out += alphabet[bytes[i]! % alphabet.length];
  return out;
}

async function provisionUser(): Promise<void> {
  if (s.busy) return;
  if (appState.user?.role !== "SUPER_USER") {
    showToast(t("Only the Super User can provision accounts directly", "Super User のみアカウントを直接作成できます"), "error");
    return;
  }
  if (!s.provName || !s.provEmail) {
    showToast(t("Full name and email are required", "氏名とメールアドレスは必須です"), "error");
    return;
  }
  const pw = s.provPassword || generateRandomPassword(18);
  if (pw.length < 12) {
    showToast(t("Generated/typed password must be 12+ chars", "パスワードは 12 文字以上必要です"), "error");
    return;
  }
  s.busy = true;
  rerender();
  try {
    // Full bootstrap done CLIENT-SIDE so the password never crosses the
    // wire to the server (zero-knowledge preserved). The role param skips
    // the SU-only signing keypair generation for ADMIN/EMPLOYEE.
    // For non-SU roles the same address is used for primary + recovery
    // (Admin/Employee don't have a separate recovery email).
    const personalEmail = s.provEmail;
    const boot = await bootstrapGenesisLocal({
      email: s.provEmail,
      personal_email: personalEmail,
      name: s.provName,
      password: pw,
      role: s.provRole,
    });
    await api.provisionUser({
      email: s.provEmail.toLowerCase(),
      name: s.provName,
      personal_email: personalEmail.toLowerCase(),
      role: s.provRole,
      kdf_algo: boot.body.kdf_algo,
      kdf_params_json: boot.body.kdf_params_json,
      kdf_salt_b64: boot.body.kdf_salt_b64,
      ecdh_pub_jwk: boot.body.ecdh_pub_jwk,
      wrapped_priv_b64: boot.body.wrapped_priv_b64,
      wrapped_ask_b64: boot.body.wrapped_ask_b64,
      wrapped_metadata_key_b64: boot.body.wrapped_metadata_key_b64,
      pubkey_sig_b64: boot.body.pubkey_sig_b64,
      master_password_hash_b64: boot.body.master_password_hash_b64,
    });
    s.provResult = { email: s.provEmail, password: pw, role: s.provRole };
    s.provName = ""; s.provEmail = ""; s.provPassword = "";
    s.loaded.users = false;
    await load("users");
    showToast(t("Account provisioned. Copy the credentials below.", "アカウントを作成しました。下記の認証情報をコピーしてください。"));
  } catch (err) {
    showToast((err as Error).message, "error");
  } finally {
    s.busy = false;
    rerender();
  }
}

async function load(tab: Tab): Promise<void> {
  if (s.loaded[tab]) return;
  try {
    if (tab === "users") s.users = (await api.usersList()).users as unknown as UserRow[];
    if (tab === "devices") s.devices = (await api.deviceListAll()).devices as unknown as DeviceRow[];
    if (tab === "audit") s.audit = (await api.audit({ limit: 200 })).rows as unknown as AuditRow[];
    if (tab === "sessions") s.sessions = (await api.sessionsListAll()).sessions as unknown as SessionRow[];
    if (tab === "links") s.links = (await api.usefulLinksList()).links;
    if (tab === "categories") s.categories = (await api.categories()).categories as unknown as CategoryRow[];
    s.loaded[tab] = true;
  } catch (err) {
    showToast((err as Error).message, "error");
  }
  rerender();
}

async function createUsefulLink(): Promise<void> {
  if (s.busy) return;
  if (!s.newLink.label_en || !s.newLink.url) {
    showToast(t("Label and URL are required", "ラベルと URL は必須です"), "error");
    return;
  }
  s.busy = true;
  try {
    await api.usefulLinkCreate({
      label_en: s.newLink.label_en,
      label_jp: s.newLink.label_jp || null,
      url: s.newLink.url,
      category: s.newLink.category || null,
      icon_name: s.newLink.icon_name || null,
      order_index: 0,
    });
    s.newLink = { label_en: "", label_jp: "", url: "", category: "", icon_name: "" };
    s.loaded.links = false;
    await load("links");
    showToast(t("Link added", "リンクを追加しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
  finally { s.busy = false; rerender(); }
}

async function updateUsefulLink(): Promise<void> {
  if (s.busy || !s.editingLink) return;
  const e = s.editingLink;
  s.busy = true;
  try {
    await api.usefulLinkUpdate(e.id, {
      label_en: e.label_en,
      label_jp: e.label_jp || null,
      url: e.url,
      category: e.category || null,
      icon_name: e.icon_name || null,
      order_index: e.order_index,
    });
    s.editingLink = null;
    s.loaded.links = false;
    await load("links");
    showToast(t("Link updated", "リンクを更新しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
  finally { s.busy = false; rerender(); }
}

async function patchCategory(id: string, patch: { default_brand_color?: string; icon_name?: string }): Promise<void> {
  try {
    await api.categoryUpdate(id, patch);
    s.loaded.categories = false;
    await load("categories");
    showToast(t("Category updated", "カテゴリを更新しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
}

const SPRITE_ICONS = [
  "ico-google-g", "ico-gmail", "ico-microsoft", "ico-dropbox", "ico-wix",
  "ico-line", "ico-chrome", "ico-meta", "ico-airbnb", "ico-viator", "ico-klook",
  "ico-bokun", "ico-byfood", "ico-gyg", "ico-booking", "ico-stripe", "ico-canva",
  "ico-mailchimp", "ico-wifi", "ico-building", "ico-windows", "ico-generic",
];

async function deleteUsefulLink(id: string, label: string): Promise<void> {
  if (!confirm(t(`Delete "${label}"?`, `「${label}」を削除しますか？`))) return;
  try {
    await api.usefulLinkDelete(id);
    s.loaded.links = false;
    await load("links");
    showToast(t("Link deleted", "リンクを削除しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
}

async function revokeSession(id: string): Promise<void> {
  if (!confirm(t(
    "Revoke this session? The user will be signed out immediately.",
    "このセッションを取り消しますか？該当ユーザーは直ちにサインアウトされます。",
  ))) return;
  try {
    await api.sessionRevoke(id);
    s.loaded.sessions = false;
    await load("sessions");
    showToast(t("Session revoked", "セッションを取り消しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
}

function describeUA(ua: string | null): string {
  if (!ua) return t("(unknown)", "(不明)");
  // Cheap, no UA-library: extract platform + browser hint.
  const m = ua.match(/(?:Windows|Mac OS|Linux|Android|iPhone|iPad|iPod)[^\s;)]*/);
  const b = ua.match(/(?:Chrome|Firefox|Safari|Edge|OPR|Opera|SamsungBrowser)\/[\d.]+/);
  if (m && b) return `${m[0]} - ${b[0].split("/")[0]}`;
  if (m) return m[0];
  if (b) return b[0].split("/")[0]!;
  // Non-browser UAs (CLI tools, bots, scripts) - label them rather than
  // showing "? - ?" which looks like a render bug.
  if (/\b(?:bun|node|deno)\b/i.test(ua)) return t("CLI script", "CLI スクリプト");
  if (/\b(?:curl|wget|httpie)\b/i.test(ua)) return t("HTTP client", "HTTP クライアント");
  if (/\bGoogleBot|bingbot|crawler|spider\b/i.test(ua)) return t("Bot / crawler", "ボット");
  // Fallback: show the first ~40 chars of the raw UA so it's at least
  // something an admin can recognize, instead of question marks.
  return ua.length > 40 ? ua.slice(0, 40) + "…" : ua;
}

function sessionsTable(): TemplateResult {
  if (!s.sessions.length) return html`<p>
    <span data-lang="en">No active sessions.</span><span data-lang="jp">アクティブなセッションはありません。</span>
  </p>`;
  return html`
    <table class="adm-table">
      <thead><tr>
        <th><span data-lang="en">User</span><span data-lang="jp">ユーザー</span></th>
        <th><span data-lang="en">Browser / OS</span><span data-lang="jp">ブラウザ / OS</span></th>
        <th>IP</th>
        <th><span data-lang="en">Logged in</span><span data-lang="jp">ログイン時刻</span></th>
        <th><span data-lang="en">Last seen</span><span data-lang="jp">最終アクセス</span></th>
        <th><span data-lang="en">Expires</span><span data-lang="jp">有効期限</span></th>
        <th><span data-lang="en">Action</span><span data-lang="jp">操作</span></th>
      </tr></thead>
      <tbody>
        ${s.sessions.map((row) => html`<tr>
          <td>${row.user_email ?? row.user_id}</td>
          <td>${describeUA(row.ua)}</td>
          <td style="font-family: ui-monospace, Consolas, monospace; font-size: 11px;">${row.ip ?? "-"}</td>
          <td>${dateShort(row.created_at)}</td>
          <td>${dateShort(row.last_used_at)}</td>
          <td>${dateShort(row.expires_at)}</td>
          <td class="actions">
            <button class="ghost-btn" @click=${() => void revokeSession(row.id)}>
              <span data-lang="en">Revoke</span><span data-lang="jp">取消</span>
            </button>
          </td>
        </tr>`)}
      </tbody>
    </table>
  `;
}

function setTab(tab: Tab): void { s.tab = tab; rerender(); void load(tab); }

async function createInvite(): Promise<void> {
  if (s.busy || !s.inviteEmail || !s.inviteName) {
    showToast(t("Email and full name are required", "メールアドレスと氏名は必須です"), "error"); return;
  }
  s.busy = true;
  try {
    const r = await api.createInvite({
      email: s.inviteEmail.toLowerCase(),
      name: s.inviteName,
      personal_email: s.inviteEmail.toLowerCase(),
      role: s.inviteRole,
    });
    const exp = new Date(r.expires_at).toLocaleString(appState.lang === "en" ? "en-GB" : "ja-JP");
    s.inviteOut = `Invite ID: ${r.invite_id}\nExpires: ${exp}\nShare invite URL: ${window.location.origin}/invite/${r.invite_id}#k=<one-time-key-generated-clientside>`;
    showToast(t("Invite created", "招待を作成しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
  finally { s.busy = false; rerender(); }
}

async function approveDevice(id: string): Promise<void> {
  try {
    await api.deviceApprove(id);
    s.loaded.devices = false;
    await load("devices");
    showToast(t("Device approved", "デバイスを承認しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
}

async function revokeDevice(id: string): Promise<void> {
  if (!confirm(t(
    "Revoke this device? The user will not be able to use it for future logins.",
    "このデバイスを取り消しますか？ユーザーは今後このデバイスでログインできなくなります。",
  ))) return;
  try {
    await api.deviceRevoke(id);
    s.loaded.devices = false;
    await load("devices");
    showToast(t("Device revoked", "デバイスを取り消しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
}

async function renameDevice(id: string, current: string | null): Promise<void> {
  const name = prompt(t("Device display name:", "デバイスの表示名:"), current ?? "");
  if (!name) return;
  try {
    await api.deviceRename(id, name);
    s.loaded.devices = false;
    await load("devices");
    showToast(t("Device renamed", "デバイス名を変更しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
}

async function sendDeviceApprovalLink(id: string): Promise<void> {
  try {
    const r = await api.deviceRequestApproval(id);
    if (r.dispatched && r.email_to) {
      showToast(t(
        `Approval link sent to ${r.email_to}`,
        `承認リンクを ${r.email_to} に送信しました`,
      ));
    } else if (r.token_b64url) {
      const link = `${window.location.origin}/approve-device?token=${r.token_b64url}`;
      copyToClipboard(link);
      showToast(t(
        "Email channel unavailable - link copied to clipboard",
        "メール送信不可 - リンクをクリップボードにコピーしました",
      ));
    } else {
      showToast(t("Failed to send approval link", "承認リンクの送信に失敗しました"), "error");
    }
  } catch (err) { showToast((err as Error).message, "error"); }
}

async function blockUser(id: string, block: boolean): Promise<void> {
  const msg = block
    ? t("Block this user? All sessions will be revoked immediately.", "このユーザーをブロックしますか？すべてのセッションが直ちに取り消されます。")
    : t("Unblock this user?", "このユーザーのブロックを解除しますか？");
  if (!confirm(msg)) return;
  try {
    await api.userBlock(id, block);
    s.loaded.users = false;
    await load("users");
    showToast(block ? t("User blocked", "ユーザーをブロックしました") : t("User unblocked", "ユーザーのブロックを解除しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
}

async function issueResetLink(id: string, email: string): Promise<void> {
  if (!confirm(t(
    `Send password reset link to ${email}? All their sessions will be revoked.`,
    `${email} にパスワードリセットリンクを送信しますか？該当ユーザーのすべてのセッションが取り消されます。`,
  ))) return;
  try {
    const r = await api.userIssueResetLink(id);
    showToast(t(
      `Reset link sent to ${r.email_to}`,
      `リセットリンクを ${r.email_to} に送信しました`,
    ));
  } catch (err) { showToast((err as Error).message, "error"); }
}

async function deleteUser(id: string, email: string): Promise<void> {
  if (!confirm(t(
    `Soft-delete ${email}? Their vault items remain but they cannot log in.`,
    `${email} をソフト削除しますか？Vault のアイテムは残りますが、ログイン不可となります。`,
  ))) return;
  try {
    await api.userDelete(id);
    s.loaded.users = false;
    await load("users");
    showToast(t("User soft-deleted", "ユーザーをソフト削除しました"));
  } catch (err) { showToast((err as Error).message, "error"); }
}

/**
 * SU-only: re-wrap every vault item the target user is a recipient of,
 * binding the wraps to the target's CURRENT ECDH pubkey. Use after a
 * password reset (which rotates the user's ECDH keypair) to restore
 * their access to existing items.
 *
 * Flow per item:
 *   1. SU reads the item (server returns ciphertext + SU's own wrap).
 *   2. SU decrypts the Content Key locally with their wrap.
 *   3. SU fetches the canonical recipient set (current pubkeys).
 *   4. SU re-encrypts the CK to every current recipient (including the
 *      reset user with their new pubkey).
 *   5. SU POSTs the new wrap set + item id to /api/items/:id/rewrap.
 */
async function rewrapItemsForUser(_userId: string, email: string): Promise<void> {
  if (!confirm(t(
    `Re-wrap all vault items for ${email}? Run this after the user completes a password reset so their existing items become readable with the new keypair.`,
    `${email} のすべての Vault アイテムを再ラップしますか？パスワードリセット完了後に実行することで、既存のアイテムが新しい鍵ペアで読めるようになります。`,
  ))) return;
  const unlocked = (window as unknown as { __unlocked?: UnlockedState }).__unlocked;
  if (!unlocked) {
    showToast(t("Your vault is locked - sign in again first", "Vault がロックされています - 再ログインしてください"), "error");
    return;
  }
  try {
    const recipients = await api.recipients();
    const recList = recipients.recipients.map((r) => ({
      user_id: r.user_id as string,
      ecdh_pub_jwk: r.ecdh_pub_jwk as string,
    }));
    const itemsList = await api.itemsList();
    const items = itemsList.items as Array<{ id: string; scope: "personal" | "company"; version: number }>;
    const total = items.length;
    let okCount = 0;
    const failures: Array<{ id: string; reason: string }> = [];
    let idx = 0;
    for (const itemMeta of items) {
      idx++;
      try {
        const detail = await api.itemRead(itemMeta.id);
        const decrypted = await decryptItem({
          item_id: itemMeta.id,
          scope: itemMeta.scope,
          version: itemMeta.version,
          ciphertext_b64: detail.item.ciphertext_b64 as string,
          iv_b64: detail.item.iv_b64 as string,
          name_ct_b64: detail.item.name_ct_b64 as string,
          name_iv_b64: detail.item.name_iv_b64 as string,
          ephemeral_pub_jwk: detail.wrap.ephemeral_pub_jwk,
          wrapped_ck_b64: detail.wrap.wrapped_ck_b64,
          unlocked,
        });
        const reenc = await encryptItem({
          item_id: itemMeta.id,
          scope: itemMeta.scope,
          version: itemMeta.version,
          name: decrypted.name,
          value: decrypted.value,
          recipients: recList,
          unlocked,
        });
        await api.itemRewrap(itemMeta.id, { wraps: reenc.wraps });
        okCount++;
        // eslint-disable-next-line no-console
        console.log(`[rewrap] ${idx}/${total} OK: ${decrypted.name}`);
      } catch (e) {
        const reason = (e as Error).message ?? String(e);
        failures.push({ id: itemMeta.id, reason });
        // eslint-disable-next-line no-console
        console.error(`[rewrap] ${idx}/${total} FAIL: ${itemMeta.id} - ${reason}`);
      }
    }
    if (failures.length === 0) {
      showToast(t(`Re-wrapped ${okCount} items`, `${okCount} 件のアイテムを再ラップしました`));
    } else {
      const detailMsg = failures.slice(0, 3).map((f) => `${f.id.slice(0, 8)}: ${f.reason}`).join(" | ");
      const more = failures.length > 3 ? ` (+${failures.length - 3} more, see console)` : "";
      showToast(
        t(
          `Re-wrapped ${okCount}/${total}. Failures: ${detailMsg}${more}`,
          `${okCount}/${total} 件成功。失敗: ${detailMsg}${more}`,
        ),
        "error",
      );
    }
  } catch (err) {
    showToast((err as Error).message, "error");
  }
}

async function forceDisableTotp(id: string, email: string): Promise<void> {
  if (!confirm(t(
    `Force-disable 2FA for ${email}? All their sessions will be revoked and the user MUST re-enroll at next login.`,
    `${email} の 2FA を強制的に無効化しますか？該当ユーザーのすべてのセッションが取り消され、次回ログイン時に再登録が必要となります。`,
  ))) return;
  try {
    const r = await api.userForceDisableTotp(id);
    s.loaded.users = false;
    await load("users");
    showToast(r.was_enrolled
      ? t("2FA force-disabled. User must re-enroll.", "2FA を強制無効化しました。ユーザーは再登録が必要です。")
      : t("User had no 2FA enrolled.", "ユーザーは 2FA を登録していませんでした。"));
  } catch (err) { showToast((err as Error).message, "error"); }
}

async function changeUserRole(id: string, current: string): Promise<void> {
  const next = current === "ADMIN" ? "EMPLOYEE" : "ADMIN";
  if (!confirm(t(`Change role from ${current} to ${next}?`, `ロールを ${current} から ${next} へ変更しますか？`))) return;
  try {
    await api.userPatch(id, next as "ADMIN" | "EMPLOYEE");
    s.loaded.users = false;
    await load("users");
    showToast(t(`Role changed to ${next}`, `ロールを ${next} に変更しました`));
  } catch (err) { showToast((err as Error).message, "error"); }
}

function dateShort(ms: number | null | undefined): string {
  if (!ms) return "-";
  const locale = appState.lang === "en" ? "en-GB" : "ja-JP";
  return new Date(ms).toLocaleString(locale, {
    year: "2-digit", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function usersTable(): TemplateResult {
  if (!s.users.length) return html`<p>
    <span data-lang="en">No users.</span><span data-lang="jp">ユーザーがいません。</span>
  </p>`;
  return html`
    <table class="adm-table">
      <thead><tr>
        <th><span data-lang="en">Email</span><span data-lang="jp">メール</span></th>
        <th><span data-lang="en">Name</span><span data-lang="jp">氏名</span></th>
        <th><span data-lang="en">Role</span><span data-lang="jp">ロール</span></th>
        <th>2FA</th>
        <th><span data-lang="en">Status</span><span data-lang="jp">状態</span></th>
        <th><span data-lang="en">Created</span><span data-lang="jp">作成日</span></th>
        <th><span data-lang="en">Actions</span><span data-lang="jp">操作</span></th>
      </tr></thead>
      <tbody>
        ${s.users.map((u) => html`
          <tr>
            <td>${u.email}</td>
            <td>${u.name}</td>
            <td>${u.role}</td>
            <td>${u.totp_enrolled_at
              ? html`<span class="badge is-approved"><span data-lang="en">on</span><span data-lang="jp">有効</span></span>`
              : html`<span class="badge is-pending"><span data-lang="en">off</span><span data-lang="jp">無効</span></span>`}</td>
            <td>
              ${u.is_blocked === 1
                ? html`<span class="badge is-blocked"><span data-lang="en">blocked</span><span data-lang="jp">ブロック中</span></span>`
                : u.reset_pending
                  ? html`<span class="badge is-pending"><span data-lang="en">reset pending</span><span data-lang="jp">リセット保留</span></span>`
                  : html`<span class="badge is-approved"><span data-lang="en">active</span><span data-lang="jp">有効</span></span>`}
            </td>
            <td>${dateShort(u.created_at)}</td>
            <td class="actions">
              ${u.role !== "SUPER_USER" ? html`
                <button class="ghost-btn" @click=${() => void issueResetLink(u.id, u.email)}>
                  <span data-lang="en">Reset link</span><span data-lang="jp">リセットリンク</span>
                </button>
                ${appState.user?.role === "SUPER_USER"
                  ? html`<button class="ghost-btn" @click=${() => void rewrapItemsForUser(u.id, u.email)}>
                      <span data-lang="en">Re-wrap items</span><span data-lang="jp">アイテム再ラップ</span>
                    </button>`
                  : nothing}
                ${u.totp_enrolled_at
                  ? html`<button class="ghost-btn" @click=${() => void forceDisableTotp(u.id, u.email)}>
                      <span data-lang="en">Reset 2FA</span><span data-lang="jp">2FA リセット</span>
                    </button>`
                  : nothing}
                <button class="ghost-btn" @click=${() => void changeUserRole(u.id, u.role)}>
                  ${u.role === "ADMIN"
                    ? html`<span data-lang="en">${"→"} Employee</span><span data-lang="jp">${"→"} 社員</span>`
                    : html`<span data-lang="en">${"→"} Admin</span><span data-lang="jp">${"→"} 管理者</span>`}
                </button>
                ${u.is_blocked === 1
                  ? html`<button class="ghost-btn" @click=${() => void blockUser(u.id, false)}>
                      <span data-lang="en">Unblock</span><span data-lang="jp">解除</span>
                    </button>`
                  : html`<button class="ghost-btn" @click=${() => void blockUser(u.id, true)}>
                      <span data-lang="en">Block</span><span data-lang="jp">ブロック</span>
                    </button>`}
                <button class="ghost-btn" @click=${() => void deleteUser(u.id, u.email)}>
                  <span data-lang="en">Delete</span><span data-lang="jp">削除</span>
                </button>
              ` : nothing}
            </td>
          </tr>
        `)}
      </tbody>
    </table>
  `;
}

function devicesTable(): TemplateResult {
  if (!s.devices.length) return html`<p>
    <span data-lang="en">No devices yet.</span><span data-lang="jp">登録されたデバイスはまだありません。</span>
  </p>`;
  return html`
    <table class="adm-table">
      <thead><tr>
        <th><span data-lang="en">Name / Label</span><span data-lang="jp">名称 / ラベル</span></th>
        <th><span data-lang="en">Owner</span><span data-lang="jp">所有者</span></th>
        <th>UA</th>
        <th><span data-lang="en">Status</span><span data-lang="jp">状態</span></th>
        <th><span data-lang="en">Last used</span><span data-lang="jp">最終使用</span></th>
        <th><span data-lang="en">Created</span><span data-lang="jp">作成日</span></th>
        <th><span data-lang="en">Actions</span><span data-lang="jp">操作</span></th>
      </tr></thead>
      <tbody>
        ${s.devices.map((d) => {
          const display = d.device_name ?? d.device_label ?? t("(unnamed)", "(名称未設定)");
          return html`<tr>
            <td>${display}</td>
            <td>${d.user_email ?? d.user_id}</td>
            <td style="max-width: 240px; overflow: hidden; text-overflow: ellipsis; color: var(--text-2); font-size: 11px;">${(d.user_agent ?? "").slice(0, 60)}</td>
            <td><span class="badge is-${d.status}">${d.status}</span></td>
            <td>${dateShort(d.last_used_at)}</td>
            <td>${dateShort(d.created_at)}</td>
            <td class="actions">
              ${d.status === "pending" ? html`
                <button class="primary" @click=${() => void approveDevice(d.id)}>
                  <span data-lang="en">Approve</span><span data-lang="jp">承認</span>
                </button>
                <button class="ghost-btn" @click=${() => void sendDeviceApprovalLink(d.id)}>
                  <span data-lang="en">Magic link</span><span data-lang="jp">マジックリンク</span>
                </button>
              ` : nothing}
              <button class="ghost-btn" @click=${() => void renameDevice(d.id, d.device_name)}>
                <span data-lang="en">Rename</span><span data-lang="jp">名称変更</span>
              </button>
              ${d.status !== "revoked" ? html`
                <button class="ghost-btn" @click=${() => void revokeDevice(d.id)}>
                  <span data-lang="en">Revoke</span><span data-lang="jp">取消</span>
                </button>
              ` : nothing}
            </td>
          </tr>`;
        })}
      </tbody>
    </table>
  `;
}

function auditTable(): TemplateResult {
  if (!s.audit.length) return html`<p>
    <span data-lang="en">No audit events.</span><span data-lang="jp">監査イベントはありません。</span>
  </p>`;
  return html`
    <table class="adm-table">
      <thead><tr>
        <th><span data-lang="en">When</span><span data-lang="jp">日時</span></th>
        <th><span data-lang="en">Actor</span><span data-lang="jp">実行者</span></th>
        <th><span data-lang="en">Action</span><span data-lang="jp">操作</span></th>
        <th><span data-lang="en">Resource</span><span data-lang="jp">対象</span></th>
        <th>IP</th>
      </tr></thead>
      <tbody>
        ${s.audit.map((r) => html`<tr>
          <td>${dateShort(r.at)}</td>
          <td style="font-family: ui-monospace, Consolas, monospace; font-size: 11px;">${(r.actor_id ?? "(system)").slice(0, 8)}</td>
          <td><span class="badge ${r.action.includes("FAIL") ? "is-revoked" : r.action.includes("LOGIN") ? "is-approved" : "is-pending"}">${r.action}</span></td>
          <td style="font-family: ui-monospace, Consolas, monospace; font-size: 11px;">${(r.item_id ?? "-").slice(0, 8)}</td>
          <td>${r.ip ?? "-"}</td>
        </tr>`)}
      </tbody>
    </table>
  `;
}

function provisionPanel(): TemplateResult {
  if (appState.user?.role !== "SUPER_USER") return html``;
  return html`
    <div class="card-panel" style="max-width: 560px; margin-bottom: 20px;">
      <h2 class="section-h2">
        <span data-lang="en">Provision user directly (Super User only)</span><span data-lang="jp">ユーザー直接作成 (Super User 限定)</span>
      </h2>
      <p class="section-help">
        <span data-lang="en">Create an account, set a temporary master password, share it with the user out-of-band. The user is forced to change it on first login. Encryption stays end-to-end: the password never crosses the wire to the server.</span><span data-lang="jp">アカウントを作成し、仮マスターパスワードを設定して別経路でユーザーに伝えます。初回ログイン時にパスワード変更が必須となります。E2E 暗号化は維持され、パスワードはサーバーに送信されません。</span>
      </p>
      <div class="form-row"><label><span data-lang="en">Full name</span><span data-lang="jp">氏名</span></label>
        <input .value=${s.provName} @input=${(e: InputEvent) => (s.provName = (e.target as HTMLInputElement).value)} /></div>
      <div class="form-row"><label><span data-lang="en">Email</span><span data-lang="jp">メール</span></label>
        <input type="email" .value=${s.provEmail} @input=${(e: InputEvent) => (s.provEmail = (e.target as HTMLInputElement).value)} /></div>
      <div class="form-row"><label><span data-lang="en">Role</span><span data-lang="jp">ロール</span></label>
        <select @change=${(e: Event) => (s.provRole = (e.target as HTMLSelectElement).value as "EMPLOYEE" | "ADMIN")}>
          <option value="EMPLOYEE">EMPLOYEE</option>
          <option value="ADMIN">ADMIN</option>
        </select>
      </div>
      <div class="form-row"><label>
          <span data-lang="en">Temporary master password (leave empty to auto-generate)</span><span data-lang="jp">仮マスターパスワード (空欄で自動生成)</span>
        </label>
        <input type="text" .value=${s.provPassword}
          placeholder="${appState.lang === "en" ? "auto-generate 18 chars" : "自動生成 18 文字"}"
          @input=${(e: InputEvent) => (s.provPassword = (e.target as HTMLInputElement).value)} /></div>
      <button class="primary" ?disabled=${s.busy} @click=${() => void provisionUser()}>
        ${s.busy
          ? html`<span data-lang="en">Provisioning…</span><span data-lang="jp">作成中…</span>`
          : html`<span data-lang="en">Create account</span><span data-lang="jp">アカウント作成</span>`}
      </button>
      ${s.provResult ? html`
        <div style="background: var(--surface-2); padding: 14px; margin-top: 14px; border-radius: 8px; border: 1px solid var(--border-1);">
          <p style="font-weight: 700; margin: 0 0 8px;">
            <span data-lang="en">Account created. Copy the credentials below and share them with the user. They will not be shown again.</span><span data-lang="jp">アカウントを作成しました。下記の認証情報をコピーしてユーザーに共有してください。二度と表示されません。</span>
          </p>
          <div class="kv-row"><label>Email</label><div class="kv-val mono">${s.provResult.email}</div></div>
          <div class="kv-row"><label>Role</label><div class="kv-val mono">${s.provResult.role}</div></div>
          <div class="kv-row"><label>Master password</label><div class="kv-val mono">${s.provResult.password}</div></div>
          <div style="margin-top: 10px; display: flex; gap: 8px;">
            <button class="ghost-btn" @click=${() => copyToClipboard(`Email: ${s.provResult!.email}\nPassword: ${s.provResult!.password}\nLogin: ${window.location.origin}/`)}>
              <span data-lang="en">Copy all</span><span data-lang="jp">すべてコピー</span>
            </button>
            <button class="ghost-btn" @click=${() => { s.provResult = null; rerender(); }}>
              <span data-lang="en">I have shared it</span><span data-lang="jp">共有済み</span>
            </button>
          </div>
        </div>
      ` : nothing}
    </div>
  `;
}

function invitesPanel(): TemplateResult {
  return html`
    ${provisionPanel()}
    <div class="card-panel" style="max-width: 560px;">
      <h2 class="section-h2">
        <span data-lang="en">New invite link</span><span data-lang="jp">新規招待リンク</span>
      </h2>
      <p class="section-help">
        <span data-lang="en">Generate an invite link for a new team member. They set their own master password on first click. Admins can only use this path; they cannot provision accounts directly.</span><span data-lang="jp">新しいチームメンバー向けの招待リンクを生成します。クリック時にユーザーがマスターパスワードを設定します。Admin はこのパスのみ利用可能で、直接アカウント作成はできません。</span>
      </p>
      <div class="form-row"><label>
          <span data-lang="en">Full name</span><span data-lang="jp">氏名</span>
        </label>
        <input .value=${s.inviteName}
          @input=${(e: InputEvent) => (s.inviteName = (e.target as HTMLInputElement).value)} /></div>
      <div class="form-row"><label>
          <span data-lang="en">Email</span><span data-lang="jp">メール</span>
        </label>
        <input type="email" .value=${s.inviteEmail}
          @input=${(e: InputEvent) => (s.inviteEmail = (e.target as HTMLInputElement).value)} /></div>
      <div class="form-row"><label>
          <span data-lang="en">Role</span><span data-lang="jp">ロール</span>
        </label>
        <select @change=${(e: Event) => (s.inviteRole = (e.target as HTMLSelectElement).value as "EMPLOYEE" | "ADMIN")}>
          <option value="EMPLOYEE">EMPLOYEE</option>
          <option value="ADMIN">ADMIN</option>
        </select>
      </div>
      <button class="primary" ?disabled=${s.busy} @click=${() => void createInvite()}>
        ${s.busy
          ? html`<span data-lang="en">Generating…</span><span data-lang="jp">生成中…</span>`
          : html`<span data-lang="en">Create invite</span><span data-lang="jp">招待を作成</span>`}
      </button>
      ${s.inviteOut ? html`
        <pre style="font-family: ui-monospace, Consolas, monospace; background: var(--surface-2); padding: 12px; margin-top: 16px; border-radius: 8px; font-size: 12px; white-space: pre-wrap; word-break: break-all;">${s.inviteOut}</pre>
      ` : nothing}
    </div>
  `;
}

function linksTable(): TemplateResult {
  return html`
    <div class="card-panel" style="max-width: 720px; margin-bottom: 20px;">
      <h2 class="section-h2">
        <span data-lang="en">Add useful link</span><span data-lang="jp">便利リンク追加</span>
      </h2>
      <p class="section-help">
        <span data-lang="en">These links appear in the vault footer for every user (Chrome, Gmail, LINE, schedules, etc.).</span><span data-lang="jp">これらのリンクは全ユーザーの Vault フッターに表示されます (Chrome / Gmail / LINE / スケジュール等)。</span>
      </p>
      <div class="form-row"><label><span data-lang="en">Label (EN)</span><span data-lang="jp">ラベル (EN)</span></label>
        <input .value=${s.newLink.label_en} @input=${(e: InputEvent) => (s.newLink.label_en = (e.target as HTMLInputElement).value)} /></div>
      <div class="form-row"><label><span data-lang="en">Label (JP, optional)</span><span data-lang="jp">ラベル (JP, 任意)</span></label>
        <input .value=${s.newLink.label_jp} @input=${(e: InputEvent) => (s.newLink.label_jp = (e.target as HTMLInputElement).value)} /></div>
      <div class="form-row"><label>URL</label>
        <input type="url" .value=${s.newLink.url} @input=${(e: InputEvent) => (s.newLink.url = (e.target as HTMLInputElement).value)} /></div>
      <div class="form-row"><label><span data-lang="en">Category (optional)</span><span data-lang="jp">カテゴリ (任意)</span></label>
        <input .value=${s.newLink.category} @input=${(e: InputEvent) => (s.newLink.category = (e.target as HTMLInputElement).value)}
          placeholder="${appState.lang === "en" ? "e.g. Tools, Communication" : "例: ツール、コミュニケーション"}" /></div>
      <button class="primary" ?disabled=${s.busy} @click=${() => void createUsefulLink()}>
        ${s.busy ? html`<span data-lang="en">Adding…</span><span data-lang="jp">追加中…</span>` : html`<span data-lang="en">Add link</span><span data-lang="jp">リンク追加</span>`}
      </button>
    </div>
    ${s.links.length === 0
      ? html`<p><span data-lang="en">No useful links yet.</span><span data-lang="jp">便利リンクはまだありません。</span></p>`
      : html`<table class="adm-table">
          <thead><tr>
            <th><span data-lang="en">Label</span><span data-lang="jp">ラベル</span></th>
            <th>URL</th>
            <th><span data-lang="en">Category</span><span data-lang="jp">カテゴリ</span></th>
            <th><span data-lang="en">Actions</span><span data-lang="jp">操作</span></th>
          </tr></thead>
          <tbody>
            ${s.links.map((l) => s.editingLink?.id === l.id
              ? html`<tr>
                  <td><input .value=${s.editingLink!.label_en} @input=${(e: InputEvent) => (s.editingLink!.label_en = (e.target as HTMLInputElement).value)} style="width: 100%;" /></td>
                  <td><input .value=${s.editingLink!.url} @input=${(e: InputEvent) => (s.editingLink!.url = (e.target as HTMLInputElement).value)} style="width: 100%;" /></td>
                  <td><input .value=${s.editingLink!.category ?? ""} @input=${(e: InputEvent) => (s.editingLink!.category = (e.target as HTMLInputElement).value || null)} style="width: 100%;" /></td>
                  <td class="actions">
                    <button class="primary" @click=${() => void updateUsefulLink()}>
                      <span data-lang="en">Save</span><span data-lang="jp">保存</span>
                    </button>
                    <button class="ghost-btn" @click=${() => { s.editingLink = null; rerender(); }}>
                      <span data-lang="en">Cancel</span><span data-lang="jp">キャンセル</span>
                    </button>
                  </td>
                </tr>`
              : html`<tr>
                  <td><b>${l.label_en}</b>${l.label_jp ? html`<br /><span style="color: var(--text-2); font-size: 11px;">${l.label_jp}</span>` : nothing}</td>
                  <td style="font-family: ui-monospace, Consolas, monospace; font-size: 11px; max-width: 360px; overflow: hidden; text-overflow: ellipsis;"><a href=${l.url} target="_blank" rel="noopener">${l.url}</a></td>
                  <td>${l.category ?? "-"}</td>
                  <td class="actions">
                    <button class="ghost-btn" @click=${() => { s.editingLink = { ...l }; rerender(); }}>
                      <span data-lang="en">Edit</span><span data-lang="jp">編集</span>
                    </button>
                    <button class="ghost-btn" @click=${() => void deleteUsefulLink(l.id, l.label_en)}>
                      <span data-lang="en">Delete</span><span data-lang="jp">削除</span>
                    </button>
                  </td>
                </tr>`)}
          </tbody>
        </table>`}
  `;
}

function categoriesTable(): TemplateResult {
  if (appState.user?.role !== "SUPER_USER") {
    return html`<p>
      <span data-lang="en">Only the Super User can edit categories.</span><span data-lang="jp">カテゴリの編集は Super User のみ可能です。</span>
    </p>`;
  }
  if (!s.categories.length) {
    return html`<p><span data-lang="en">No categories loaded.</span><span data-lang="jp">カテゴリが読み込まれていません。</span></p>`;
  }
  return html`
    <p class="section-help" style="max-width: 720px;">
      <span data-lang="en">Pick a brand colour and an icon for each category. The colour drives the section box background + the tag pill in the vault view; the icon shows next to the category name.</span><span data-lang="jp">各カテゴリのブランドカラーとアイコンを設定します。色は Vault ビューのセクション背景とタグに反映され、アイコンはカテゴリ名の横に表示されます。</span>
    </p>
    <table class="adm-table">
      <thead><tr>
        <th><span data-lang="en">Category</span><span data-lang="jp">カテゴリ</span></th>
        <th><span data-lang="en">Color</span><span data-lang="jp">色</span></th>
        <th><span data-lang="en">Icon</span><span data-lang="jp">アイコン</span></th>
        <th></th>
      </tr></thead>
      <tbody>
        ${s.categories.map((c) => {
          const label = appState.lang === "en" ? c.label_en : c.label_jp;
          return html`<tr>
            <td><b>${label}</b><br /><span style="color: var(--text-2); font-size: 11px;">${c.slug}</span></td>
            <td>
              <input type="color" .value=${c.default_brand_color}
                @change=${(e: Event) => void patchCategory(c.id, { default_brand_color: (e.target as HTMLInputElement).value })}
                style="width: 50px; height: 32px; padding: 2px; border: 1px solid var(--border-1); border-radius: 6px; cursor: pointer;" />
              <code style="margin-left: 8px; font-size: 11px; color: var(--text-2);">${c.default_brand_color}</code>
            </td>
            <td>
              <select @change=${(e: Event) => void patchCategory(c.id, { icon_name: (e.target as HTMLSelectElement).value })}
                style="min-width: 160px;">
                ${SPRITE_ICONS.map((id) => html`<option value=${id} ?selected=${c.icon_name === id}>${id}</option>`)}
              </select>
            </td>
            <td>
              <svg style="width: 28px; height: 28px;" aria-hidden="true">
                <use href="#${c.icon_name}" xlink:href="#${c.icon_name}" />
              </svg>
            </td>
          </tr>`;
        })}
      </tbody>
    </table>
  `;
}

export function renderAdmin(): TemplateResult {
  // Front-end role guard so an EMPLOYEE that reaches /admin via URL bar
  // or back button doesn't see a flash of unauthorised UI before the
  // backend rejects every fetch (ARC iter-6 finding).
  if (appState.user && appState.user.role === "EMPLOYEE") {
    return html`<main class="page">
      <h1 class="page-h1">
        <span data-lang="en">Access denied</span><span data-lang="jp">アクセス拒否</span>
      </h1>
      <p>
        <span data-lang="en">You do not have permission to view the admin console.</span><span data-lang="jp">管理コンソールを表示する権限がありません。</span>
      </p>
    </main>`;
  }
  void load(s.tab);
  return html`
    <main class="page">
      <h1 class="page-h1">
        <span data-lang="en">Administration</span><span data-lang="jp">管理</span>
      </h1>
      <nav class="adm-tabs">
        <button @click=${() => setTab("users")} ?disabled=${s.tab === "users"}>
          <span data-lang="en">Users</span><span data-lang="jp">ユーザー</span>
        </button>
        <button @click=${() => setTab("invites")} ?disabled=${s.tab === "invites"}>
          <span data-lang="en">Invites</span><span data-lang="jp">招待</span>
        </button>
        <button @click=${() => setTab("sessions")} ?disabled=${s.tab === "sessions"}>
          <span data-lang="en">Sessions</span><span data-lang="jp">セッション</span>
        </button>
        <button @click=${() => setTab("devices")} ?disabled=${s.tab === "devices"}>
          <span data-lang="en">Devices</span><span data-lang="jp">デバイス</span>
        </button>
        <button @click=${() => setTab("links")} ?disabled=${s.tab === "links"}>
          <span data-lang="en">Useful links</span><span data-lang="jp">便利リンク</span>
        </button>
        ${appState.user?.role === "SUPER_USER"
          ? html`<button @click=${() => setTab("categories")} ?disabled=${s.tab === "categories"}>
              <span data-lang="en">Categories</span><span data-lang="jp">カテゴリ</span>
            </button>`
          : nothing}
        <button @click=${() => setTab("audit")} ?disabled=${s.tab === "audit"}>Audit</button>
      </nav>
      ${s.tab === "users" ? usersTable() : nothing}
      ${s.tab === "invites" ? invitesPanel() : nothing}
      ${s.tab === "sessions" ? sessionsTable() : nothing}
      ${s.tab === "devices" ? devicesTable() : nothing}
      ${s.tab === "links" ? linksTable() : nothing}
      ${s.tab === "categories" ? categoriesTable() : nothing}
      ${s.tab === "audit" ? auditTable() : nothing}
    </main>
  `;
}
